import * as React from 'react'
import { useState } from 'react';
// ─────────────────────────────────────────────
// COMPONENT 6: ContactFormSection
// PLACEMENT: New <section id="contact"> before .brand-banner
// Usage: <ContactFormSection />
// ─────────────────────────────────────────────
export function ContactFormSection() {
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", service: "", message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Wire to your backend / CRM here
    setSubmitted(true);
  };

  return (
    <section id="contact">
      <style>{`
        #contact { position: relative; z-index: 3; padding: 120px 0; }
        .rs-contact-panel {
          position: relative;
          background: rgba(14,22,35,0.95);
          border: 1px solid #1D3145;
          border-radius: 28px;
          padding: 60px 56px;
          max-width: 880px;
          margin: 0 auto;
          overflow: hidden;
        }
        .rs-contact-panel::before {
          content: '';
          position: absolute;
          width: 500px; height: 500px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(29,161,242,0.13), transparent 70%);
          top: -250px; right: -100px;
          pointer-events: none;
        }
        .rs-contact-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-top: 36px;
        }
        .rs-fg { display: flex; flex-direction: column; gap: 8px; }
        .rs-fg.full { grid-column: 1 / -1; }
        .rs-fg label {
          font-family: 'JetBrains Mono', monospace;
          font-size: 11px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #49D6FF;
        }
        .rs-fg input,
        .rs-fg select,
        .rs-fg textarea {
          background: rgba(255,255,255,0.04);
          border: 1px solid #1D3145;
          border-radius: 12px;
          padding: 13px 17px;
          color: #F8FAFC;
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          outline: none;
          width: 100%;
          transition: border-color 0.25s, box-shadow 0.25s;
          appearance: none;
        }
        .rs-fg input::placeholder,
        .rs-fg textarea::placeholder { color: #A7B3C2; opacity: 0.5; }
        .rs-fg input:focus,
        .rs-fg select:focus,
        .rs-fg textarea:focus {
          border-color: #49D6FF;
          box-shadow: 0 0 0 3px rgba(73,214,255,0.1);
        }
        .rs-fg textarea { resize: vertical; min-height: 110px; }
        .rs-submit {
          grid-column: 1 / -1;
          background: linear-gradient(135deg, #1DA1F2, #49D6FF 55%, #8CEBFF);
          color: #04141f;
          font-family: 'Space Grotesk', sans-serif;
          font-weight: 700;
          font-size: 15px;
          padding: 15px 32px;
          border: none;
          border-radius: 14px;
          cursor: pointer;
          transition: box-shadow 0.3s, transform 0.3s cubic-bezier(.16,1,.3,1);
          display: inline-flex;
          align-items: center;
          gap: 10px;
          justify-content: center;
        }
        .rs-submit:hover {
          box-shadow: 0 0 40px rgba(73,214,255,0.5);
          transform: scale(1.03);
        }
        .rs-success {
          text-align: center;
          padding: 40px 0;
        }
        .rs-success-icon {
          font-size: 48px;
          margin-bottom: 16px;
          display: block;
        }
        .rs-success h3 {
          font-family: 'Space Grotesk', sans-serif;
          font-size: 22px;
          color: #FFFFFF;
          margin-bottom: 10px;
        }
        .rs-success p { color: #A7B3C2; font-size: 15px; }
        @media (max-width: 680px) {
          .rs-contact-grid { grid-template-columns: 1fr; }
          .rs-contact-panel { padding: 36px 24px; }
        }
      `}</style>
      <div className="container">
        <div className="section-head reveal">
          <div className="eyebrow">Let&apos;s build</div>
          <h2>Ready to automate
            what's slowing you down?</h2>
          <p>Tell us where you want to move faster. We&apos;ll map the path in one call.</p>
        </div>
        <div className="rs-contact-panel reveal">
          {submitted ? (
            <div className="rs-success">
              <span className="rs-success-icon">✓</span>
              <h3>We&apos;ll be in touch shortly.</h3>
              <p>Expect a reply within one business day. In the meantime, explore how we work in the Process section above.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="rs-contact-grid">
              <div className="rs-fg">
                <label htmlFor="firstName">First Name</label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  placeholder="Maya"
                  value={form.firstName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="rs-fg">
                <label htmlFor="lastName">Last Name</label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  placeholder="Lindqvist"
                  value={form.lastName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="rs-fg full">
                <label htmlFor="email">Work Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="maya@company.com"
                  value={form.email}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="rs-fg full">
                <label htmlFor="service">Area of Interest</label>
                <select
                  id="service"
                  name="service"
                  value={form.service}
                  onChange={handleChange}
                  required
                >
                  <option value="" disabled>Select a service…</option>
                  <option>AI Automation</option>
                  <option>ERP &amp; Odoo</option>
                  <option>Cloud &amp; DevOps</option>
                  <option>Web Development</option>
                  <option>Full Stack Engagement</option>
                </select>
              </div>
              <div className="rs-fg full">
                <label htmlFor="message">Tell us about your project</label>
                <textarea
                  id="message"
                  name="message"
                  placeholder="We're looking to automate our order-to-cash process and integrate it with our ERP…"
                  value={form.message}
                  onChange={handleChange}
                  required
                />
              </div>
              <button className="rs-submit" type="submit">
                Book a Demo →
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}